import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
  useRef,
  ReactNode,
  createContext,
  useCallback,
  useReducer
} from 'react';

import DocViewer, {
  DocViewerRenderers,
  IHeaderOverride
} from '@cyntler/react-doc-viewer';
import UTIF from 'utif';
import {Buffer} from 'buffer';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef
} from 'react-zoom-pan-pinch';
import {
  DocViewContainer,
  TextContainer,
  ZoomButton,
  AdditionalFeaturesButtonDiv,
  MainContainerDiv,
  ZoomButtonsDiv,
  PreviewerContainer,
  PanToMoveDiv
} from './CustomDocViewerStyles';

import {
  createAuditLog,
  downloadAsPdf,
  getFileData,
  getFileVersionData,
  getFileVersions,
  getFileZIPData
} from '@shared/services/service-handlers/PrivateService';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  TextField
} from '@mui/material';
import {
  DocumentScanner,
  Refresh,
  ZoomIn,
  ZoomOut,
  Title,
  West,
  East
} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {useTranslation} from 'react-i18next';
import FormConstants from '@shared/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import axios, {CancelTokenSource} from 'axios';
import React from 'react';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackbarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import SelectInput from '@components/form/select-input/SelectInput';
import {useParams} from 'react-router';
import TiffRenderer from './CustomRenderers/TiffRenderer';
import RTFRenderer from './CustomRenderers/RTFRenderer';
import TxtRenderer from './CustomRenderers/TxtRenderer';
import {LoadingButton} from '@mui/lab';
import ReactPDFRenderer from './CustomRenderers/PDFRenderer';

import {useAppContext} from '@context/AppContext';
import {useModuleContext} from '@context/ModuleContext';
import {IconButtonWrapper} from './MultiDocumentViewer/MultiDocumentViewerStyles';
import HTMLRenderer from './CustomRenderers/HTMLRenderer';
// import {HTMLViewerHandle} from './CustomViewers/HTMLViewer';
import EncryptUtils from 'src/utils/EncryptUtils';
import {environment} from '@shared/config/environment';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {ro, sr} from 'date-fns/locale';
import {indexOf} from 'lodash';

interface Document {
  ID: number | string | number[];
  LegacyApp: string;
  LegacyMRN: string;
  SourceDocumentID: string;
  FileExtensions: string[];
  IsNotes: boolean[];
  Name: string;
  IsMultiPage: string;
  Sequence: number[];
  DocumentTypeID: number;
  ChildNodes?: any[];
  HasDocuments?: boolean;
  DocumentSecurityGroupID?: string | number;
}

interface ActionViewObj {
  actionLabel: string;
  actionFunction: string;
}

interface ActionDownloadObj {
  actionLabel: string;
  actionFunction: string;
  permissions: any;
}

interface CustomDocViewerActions {
  viewActions: ActionViewObj;
  downloadActions: ActionDownloadObj;
}

interface CustomDocViewerProps {
  docData: Document;
  setDocData: Dispatch<SetStateAction<any>>;
  documentVersionSQL: string;
  documentVersionDataSQL: string;
  documentDataSql: string;
  variables: any;
  filterSchemaClause: string;
  docDownloadData: any;
  cardValues: any;
  // eslint-disable-next-line react/boolean-prop-naming
  shoulddisplaymetainfo: boolean;
  setDocDownloadData: (data: any) => void;
  setShoulddisplaymetainfo: Dispatch<SetStateAction<boolean>>;
  customDocViewerActions: CustomDocViewerActions[];
  PatientID: string;
  height?: any;
  top?: string;
  recordId?: string;
  procedure_type?: string;
  accession_number?: string;
  specimen_number_id?: string;
}
// required for zoomIn, zoomOut, and reset functions
interface ControlsProps {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// DocHeaderComponent — now a proper React.FC, lives OUTSIDE DocViewer
// ─────────────────────────────────────────────────────────────────────────────
interface DocHeaderProps {
  id: any;
  docs: any[];
  fetchingData: boolean;
  documentpageNumber: number;
  pendingPageJumpRef: React.MutableRefObject<number | null>;
  enqueueSnackbar: (msg: string, cfg: any) => void;
  getDocumentsNextData: (
    alreadyhasData?: boolean,
    pageSize?: number,
    pageCount?: number
  ) => Promise<void>;
  loadNextPage: () => void;
  onGoTo: (localIndex: number) => void;
  docViewerState: {
    documents: any[];
    currentFileNo: number;
    currentDocument: any;
    config?: any;
  };
}

const DocHeader: React.FC<DocHeaderProps> = ({
  id,
  docs,
  fetchingData,
  documentpageNumber,
  pendingPageJumpRef,
  enqueueSnackbar,
  getDocumentsNextData,
  loadNextPage,
  onGoTo,
  docViewerState: state
}) => {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const goToValueRef = useRef('');
  const goToInputRef = useRef<HTMLInputElement>(null);

  // ── helpers ──────────────────────────────────────────────────────────────
  const gotoSearch = (page: number) => {
    const targetId = Array.isArray(id) ? id[page] : page;
    const localIndex = state.documents.findIndex(
      (doc: any) => doc.index === targetId
    );
    if (localIndex === -1) {
      enqueueSnackbar('Document not loaded yet.', SnackbarConfig.getError());
      return;
    }
    onGoTo(localIndex);
  };

  const goToPage = (page: number) => {
    onGoTo(page);
  };

  // ── pending-jump effect ───────────────────────────────────────────────────
  useEffect(() => {
    if (pendingPageJumpRef.current !== null && !fetchingData) {
      const targetPageIndex = pendingPageJumpRef.current;
      const isPresent = state.documents.some(
        (doc: any) =>
          doc.index ===
          (Array.isArray(id) ? id[targetPageIndex] : targetPageIndex)
      );
      if (isPresent) {
        pendingPageJumpRef.current = null;
        gotoSearch(targetPageIndex);
      }
    }
  }, [state.documents, fetchingData]);

  // ── early-return guard ────────────────────────────────────────────────────
  if (
    (state.documents.length === 1 ||
      !state.currentDocument ||
      state.config?.header?.disableFileName) &&
    (Array.isArray(id) || typeof id === 'string') &&
    id.length === 1
  ) {
    return null;
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box
      onMouseDown={e => e.stopPropagation()}
      sx={{
        display: 'flex',
        // width: '52vw',
        // maxWidth: '52vw',
        py: 2,
        alignItems: 'center',
        gap: 1,
        position: 'sticky'
      }}>
      {/* Previous */}
      <Button
        variant="outlined"
        onClick={async () => {
          const currentIndex = Array.isArray(id)
            ? indexOf(id, (state?.currentDocument as any)?.index)
            : state.currentFileNo;

          const targetIndex = currentIndex - 1;
          if (targetIndex < 0) return;

          const targetId = Array.isArray(id) ? id[targetIndex] : targetIndex;
          const isPresentLocally = state.documents.some(
            (doc: any) => doc.index === targetId
          );

          if (isPresentLocally) {
            gotoSearch(targetIndex);
          } else {
            if (!fetchingData) {
              pendingPageJumpRef.current = targetIndex;
              const targetPageGroup = Math.floor(
                targetIndex / documentpageNumber
              );
              await getDocumentsNextData(
                true,
                documentpageNumber,
                targetPageGroup
              );
              loadNextPage();
            }
          }
        }}
        disabled={
          (Array.isArray(id)
            ? indexOf(id, (state?.currentDocument as any)?.index)
            : state.currentFileNo) <= 0
        }
        sx={{minWidth: 32, height: 32, p: 0, borderRadius: '6px'}}>
        <ArrowBackIosNewIcon sx={{fontSize: 16}} />
      </Button>

      {/* Page counter */}
      <div
        style={{
          padding: '4px 10px',
          background: '#f5f5f5ff',
          borderRadius: 6,
          border: '1px solid #ccc',
          fontSize: 13
        }}>
        Doc{' '}
        {Array.isArray(id) || typeof id === 'string'
          ? Array.isArray(id)
            ? indexOf(
                id,
                (state?.documents[state?.currentFileNo] as any)?.index
              ) + 1
            : id
          : ''}{' '}
        / {(Array.isArray(id) || typeof id === 'string') && id.length}
      </div>

      {/* Next */}
      <Button
        variant="outlined"
        onClick={async () => {
          const currentIndex = Array.isArray(id)
            ? indexOf(id, (state?.currentDocument as any)?.index)
            : state.currentFileNo;

          const totalPages = Array.isArray(id) ? id.length : 1;
          const targetIndex = currentIndex + 1;
          if (targetIndex >= totalPages) return;

          const targetId = Array.isArray(id) ? id[targetIndex] : targetIndex;
          const isPresentLocally = state.documents.some(
            (doc: any) => doc.index === targetId
          );

          if (isPresentLocally) {
            gotoSearch(targetIndex);
          } else {
            if (!fetchingData) {
              pendingPageJumpRef.current = targetIndex;
              const targetPageGroup = Math.ceil(
                targetIndex / documentpageNumber
              );
              await getDocumentsNextData(
                true,
                documentpageNumber,
                targetPageGroup
              );
              loadNextPage();
            }
          }
        }}
        disabled={
          state.currentFileNo >=
          (Array.isArray(id) || typeof id === 'string' ? id.length : 1) - 1
        }
        sx={{minWidth: 32, height: 32, p: 0, borderRadius: '6px'}}>
        <ArrowForwardIosIcon sx={{fontSize: 16}} />
      </Button>

      {/* Go-to input */}
      <TextField
        size="small"
        type="number"
        id="outlined-basic"
        label="Go to"
        variant="outlined"
        inputRef={goToInputRef}
        inputProps={{min: 1}}
        onChange={e => {
          const val = e.target.value;
          if (val === '' || val === '0' || /^0/.test(val)) {
            goToValueRef.current = '';
            e.target.value = '';
            forceUpdate();
            return;
          }
          if (/^\d+$/.test(val)) {
            goToValueRef.current = val;
            forceUpdate();
          }
        }}
        sx={{
          width: 80,
          '& input': {height: 18, fontSize: '15px', textAlign: 'center'}
        }}
      />

      {/* Go-to search button */}
      <Button
        variant="outlined"
        disabled={!goToValueRef.current}
        sx={{
          minWidth: 36,
          height: 36,
          p: 0,
          backgroundColor: goToValueRef.current
            ? 'primary.main'
            : 'transparent',
          color: goToValueRef.current ? '#fff' : 'rgba(0, 0, 0, 0.38)',
          border: goToValueRef.current
            ? 'none'
            : '1px solid rgba(0, 0, 0, 0.23)',
          '&:hover': {
            backgroundColor: goToValueRef.current
              ? 'primary.dark'
              : 'transparent'
          },
          '&.Mui-disabled': {backgroundColor: 'transparent'}
        }}
        onClick={async () => {
          const pageIndex = Number(goToValueRef.current) - 1;
          const totalPages = Array.isArray(id) ? id.length : 1;
          if (pageIndex < 0 || pageIndex >= totalPages) {
            enqueueSnackbar(
              'Please enter a valid page number.',
              SnackbarConfig.getError()
            );
            return;
          }
          const isPresentLocally = state.documents.some(
            (doc: any) =>
              doc.index === (Array.isArray(id) ? id[pageIndex] : pageIndex)
          );
          if (pageIndex >= 0 && pageIndex < totalPages && isPresentLocally) {
            gotoSearch(pageIndex);
          } else if (!isPresentLocally && !fetchingData) {
            pendingPageJumpRef.current = pageIndex;
            await getDocumentsNextData(
              true,
              documentpageNumber,
              Math.ceil(Number(goToValueRef.current) / documentpageNumber) - 1
            );
            loadNextPage();
          }
        }}>
        <SearchIcon />
      </Button>

      {/* Reset */}
      <Tooltip title="Reset to first page">
        <Button
          variant="outlined"
          onClick={() => {
            if (goToInputRef.current) goToInputRef.current.value = '';
            goToValueRef.current = '';
            forceUpdate();
            goToPage(0);
          }}
          sx={{minWidth: 36, height: 36, p: 0}}>
          <RestartAltIcon fontSize="small" />
        </Button>
      </Tooltip>

      {fetchingData && <CircularProgress size={24} sx={{ml: 2}} />}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const DocViewerContext = createContext({textSelectMode: false});
// export const HTMLViewerRefContext =
//   createContext<React.RefObject<HTMLViewerHandle> | null>(null);

const CustomDocViewer = (props: CustomDocViewerProps): ReactElement => {
  const {
    documentVersionSQL,
    documentVersionDataSQL,
    documentDataSql,
    docData,
    filterSchemaClause,
    setDocData,
    variables,
    docDownloadData,
    cardValues,
    shoulddisplaymetainfo,
    setDocDownloadData,
    setShoulddisplaymetainfo,
    customDocViewerActions,
    PatientID,
    height,
    recordId,
    procedure_type,
    accession_number,
    specimen_number_id
  } = props;

  const unSupportedFormats = FormConstants.DOCUMENT_FORMATS.unSupportedFormats;
  const SupportedFormats = FormConstants.DOCUMENT_FORMATS.SupportedFormats;
  const iframeSupportedFormats =
    FormConstants.DOCUMENT_FORMATS.iframeSupportedFormats;

  const {
    appState: {applicationName}
  } = useAppContext();
  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();

  const theme = useTheme();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const source = React.useRef<CancelTokenSource | null>(null);
  // eslint-disable-next-line prefer-const
  const {ID: id} = docData;
  const [fileExtensions, setFileExtensions] = useState(
    docData?.FileExtensions ?? []
  );
  const isHtmlFile = ['html', 'htm'].includes(
    fileExtensions[0]?.toLowerCase?.() ?? ''
  );
  const params = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const [documentpageNumber, setDocumentpageNumber] = useState(
    environment.FILE_CABINET_INIT_FETCH || 10
  );
  const [multipleDocPresent, setMultipleDocPresent] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [docDataStateCount, setDocDataStatePageCount] = useState(0);
  const [docs, setDocs] = useState<any>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [previewer, setPreviewer] = useState<ReactNode | null>(null);
  const [activeDocument, setActiveDocument] = useState({uri: ''});
  const [bufferData, setBufferData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [docVersionsData, setDocVersionsData] = useState([]);
  const [selectedDocVersion, setSelectedDocVersion] = useState('');
  const [selectedDocVersionIds, setSelectedDocVersionIds] = useState<any>([]);
  const [textSelectMode, setTextSelectMode] = useState(true);
  const [isMultiPage, setIsMultiPage] = useState(false);
  const navigationLockRef = useRef(false);
  const goToValueRef = useRef('');
  const goToInputRef = useRef<HTMLInputElement>(null);
  const ZoomInShowTextRef = useRef(0);
  // required for zoomIn, zoomOut, and reset functions
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);
  // const htmlViewerRef = useRef<HTMLViewerHandle>(null);
  const [htmlZoom, setHtmlZoom] = useState(100);
  const [htmlTextSelect, setHtmlTextSelect] = useState(false);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const pendingPageJumpRef = useRef<number | null>(null);
  const zoomIn = () => {
    // if (isHtmlFile) {
    //   htmlViewerRef.current?.zoomIn();
    //   setHtmlZoom(htmlViewerRef.current?.getZoom?.() ?? htmlZoom);
    //   return;
    // }
    transformComponentRef.current?.zoomIn();
  };

  const zoomOut = () => {
    // if (isHtmlFile) {
    //   htmlViewerRef.current?.zoomOut();
    //   setHtmlZoom(htmlViewerRef.current?.getZoom?.() ?? htmlZoom);
    //   return;
    // }
    transformComponentRef.current?.zoomOut();
  };
  // Sync input with current page when user clicks Next/Previous
  // useEffect(() => {
  //   setGoToValue(String(activeIndex + 1));
  // }, [activeIndex]);

  const resetTransform = () => {
    // if (isHtmlFile) {
    //   htmlViewerRef.current?.resetZoom();
    //   setHtmlZoom(100);
    //   return;
    // }
    transformComponentRef.current?.resetTransform();
  };
  // ── docViewerState mirrors DocViewer's internal state ─────────────────────
  const [docViewerState, setDocViewerState] = useState<{
    documents: any[];
    currentFileNo: number;
    currentDocument: any;
    config?: any;
  }>({
    documents: [],
    currentFileNo: 0,
    currentDocument: null
  });

  // Keep docViewerState in sync whenever activeDocument or docs change
  useEffect(() => {
    const currentFileNo = docs.findIndex(
      (d: any) => d.uri === activeDocument?.uri
    );
    setDocViewerState(prev => ({
      ...prev,
      documents: docs,
      currentFileNo: currentFileNo === -1 ? 0 : currentFileNo,
      currentDocument: activeDocument
    }));
  }, [activeDocument, docs]);

  const syncActiveDocumentType = (document: any) => {
    const currentFileNo = docs.findIndex((d: any) => d.uri === document?.uri);

    if (currentFileNo === -1 || !fileExtensions?.[currentFileNo]) {
      return;
    }

    const activeFileExtension = fileExtensions[currentFileNo];

    setDocData((prevState: any) => ({
      ...prevState,
      documentType: undefined,
      DocumentType: undefined,
      Type: undefined,
      FileType: activeFileExtension,
      MimeType: undefined,
      ContentType: undefined,
      FileExtensions: [activeFileExtension]
    }));
  };

  // ── onGoTo: drives DocViewer navigation via activeDocument prop
  const handleGoTo = (localIndex: number) => {
    if (localIndex < 0 || localIndex >= docs.length) return;
    const target = docs[localIndex];
    if (!target) return;
    setActiveDocument(target);
    setActiveIndex(target.index);
    syncActiveDocumentType(target);
    setDocViewerState(prev => ({
      ...prev,
      currentFileNo: localIndex,
      currentDocument: target,
      documents: docs
    }));
  };

  useEffect(() => {
    const asyncReset = async () => {
      await setDocumentpageNumber(environment.FILE_CABINET_INIT_FETCH || 10);
      await setDocDataStatePageCount(0);
      await setMultipleDocPresent(false);
    };
    asyncReset();
  }, [id]);
  useEffect(() => {
    if (!isLoading) {
      ZoomInShowTextRef.current = 0;
    }
  }, [ZoomInShowTextRef.current]);

  const getBase64Flag = (extension: string) => {
    if (extension) {
      switch (extension?.toLowerCase()) {
        case 'jpeg':
          return 'data:image/jpeg;base64,';
        case 'jpg':
          return 'data:image/jpg;base64,';
        case 'gif':
          return 'data:image/gif;base64,';
        case 'png':
          return 'data:image/png;base64,';
        case 'bmp':
          return 'data:image/bmp;base64,';
        case 'tiff':
          return 'data:image/tiff;base64,';
        case 'tif':
          return 'data:image/tif;base64,';
        case 'pdf':
          return 'data:application/pdf;base64,';
        case 'txt':
          return 'data:text/txt;base64,';
        case 'rtf':
          return 'data:image/rtf;base64,';
        case 'html':
          return 'data:text/html;base64,';
        case 'htm':
          return 'data:text/html;base64,';
        case 'doc':
          return 'data:application/msword;base64,';
        case 'docx':
          return 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,';
        default:
          return 'data:text/txt;base64,';
      }
    } else {
      return 'data:text/txt;base64,';
    }
  };

  const downloadRTF = (bufferURI: any, fileName: any) => {
    const bufferString = bufferURI.replace(
      /^(data:image\/tiff;base64|data:image\/tif;base64|data:image\/rtf;base64,)/i,
      ''
    );
    const nodeJSBuffer = Buffer.from(bufferString, 'base64');
    // Convert the Buffer to a Blob
    const buffer = Buffer.from(nodeJSBuffer);
    const blob = new Blob([buffer], {type: 'application/rtf'});

    // Create a download link
    const downloadLink = document.createElement('a');
    const blobURL = URL.createObjectURL(blob);
    downloadLink.href = blobURL;
    downloadLink.download = fileName || 'output.rtf';

    // Append the link to the document and trigger a click event
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up by removing the link and revoking the blob URL
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(blobURL);
  };

  const getBase64Data = (
    responseBufferData: any[],
    file_extensions: any[],
    ids?: any[]
  ) => {
    const base64DocsData: any = [];
    let tempText = '';
    // if (hasfiles) {
    //   tempText = ' '.repeat(documentpageNumber * docDataStateCount);
    // }
    responseBufferData.forEach((eachBuffer: any, index: number) => {
      const pageIndex = ids && ids?.length > 0 ? ids[index] : index;
      if (eachBuffer) {
        // CHANGE: Remove Buffer.from().toString('base64')
        // because eachBuffer is already the base64 string from backend
        base64DocsData.push({
          uri: `${getBase64Flag(file_extensions[index])}${eachBuffer}${tempText}`,
          index: pageIndex
        });
        tempText = tempText + ' ';
      }
    });

    if (base64DocsData.length === 0) {
      setActiveDocument({uri: ''});
      setActiveIndex(0);
      return [];
    }

    setActiveDocument(base64DocsData[0]);
    setActiveIndex(base64DocsData[0].index);
    return base64DocsData;
  };
  const getBase64NextData = (
    responseBufferData: any[],
    file_extensions: any[],
    hasfiles?: boolean,
    ids?: any[]
  ) => {
    const base64DocsData: any = [];
    let tempText = '';
    if (hasfiles) {
      tempText = ' '.repeat(documentpageNumber * docDataStateCount);
    }
    responseBufferData.forEach((eachBuffer: any, index: number) => {
      const pageIndex = hasfiles
        ? ids && ids?.length > 0
          ? ids[index]
          : index + documentpageNumber * docDataStateCount
        : index;
      if (eachBuffer) {
        // CHANGE: Use string directly
        base64DocsData.push({
          uri: `${getBase64Flag(file_extensions[index])}${eachBuffer}${tempText}`,
          index: pageIndex
        });
        tempText = tempText + ' ';
      }
    });
    // setActiveDocument(base64DocsData[0]);
    // setActiveIndex(base64DocsData[0].index);
    return base64DocsData;
  };
  const getVersionsData = async (data: any) => {
    try {
      const encryptedData = EncryptUtils.encryptedData(data);
      const responseVersions = await getFileVersions({tk: encryptedData});
      setDocVersionsData(responseVersions?.versions_data);
      const tempObj = {...responseVersions?.versions_data?.[0]};
      setDocData((prevState: any) => {
        return {...prevState, ...tempObj};
      });
      setSelectedDocVersion(responseVersions?.versions_data[0]?.Version);
    } catch (err) {
      setIsLoading(false);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        console.log('error message', errMessage);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };

  const logDocumentDownload = (dataObj: any) => {
    const version = selectedDocVersion;
    const tempObj: any = docVersionsData?.filter(
      (item: any) => item?.Version == version
    )[0];

    const versionID: any = selectedDocVersionIds?.length
      ? selectedDocVersionIds
      : tempObj?.VersionID ?? id;
    const updatedDocData: any = tempObj ? {...docData, ...tempObj} : docData;

    const docDataObj: any = {
      fileName: cardValues?.[0]?.name,
      SourceDocumentID: updatedDocData
        ? updatedDocData?.SourceDocumentID
        : (docData?.SourceDocumentID ?? ''),
      ids: versionID,
      version: version ? version : '1',
      downloadType: dataObj.downloadType,
      procedure_type,
      accession_number,
      specimen_number_id
    };

    const keyValueString = Object.entries(docDataObj)
      .map(([key, value]) => {
        if (value) return `${key}: ${JSON.stringify(value)}`;
        return null;
      })
      .filter(Boolean)
      .join(', ');
    const data = {
      module: params.module_name,
      sub_module: `${params.submodule_name ? params.submodule_name + ' -> ' : ''} ${FormConstants.COMMON.DOWNLOADED_DOCUMENT}`,
      sub_module_identifier: versionID,
      procedure_type,
      application_name: applicationName,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      event_description: keyValueString,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(params.module_name ?? '', data);
  };
  function paginateArray<T>(
    data: T[],
    pageSize: number,
    pageCount: number
  ): T[] {
    const startIndex = pageCount * pageSize;
    const endIndex = startIndex + pageSize;
    const value = data.slice(startIndex, endIndex);
    return value;
  }
  const loadNextPage = () => {
    setDocDataStatePageCount(prev => prev + 1);
  };
  const getDocumentsData = async (
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    setIsLoading(true);
    setBufferData([]);
    setActiveDocument({uri: ''});
    setActiveIndex(0);
    setDocDownloadData([]);
    setFileExtensions([]);

    const [firstElem]: any = id;

    const permissions = AuthUtils.getPermissions();

    const documentSecurityGroupId =
      updatedDocData?.DocumentSecurityGroupID ??
      docData?.DocumentSecurityGroupID;

    const documentSecurityIdExisted = Array.isArray(permissions)
      ? permissions.find((ele: any) => ele.id === documentSecurityGroupId)
      : undefined;

    const auditObj = {
      is_audit: true,
      audit_event_description:
        FormConstants.COMMON.VIEWED_DOCUMENT +
        (documentSecurityIdExisted
          ? `; User has Permission : ${documentSecurityIdExisted.function_label}`
          : '')
    };

    const data: any = {
      PatientID,
      SourceDocumentID: updatedDocData
        ? updatedDocData?.SourceDocumentID
        : (docData?.SourceDocumentID ?? ''),
      versions: documentVersionSQL,
      versionData: documentVersionDataSQL,
      sql: documentDataSql,
      variables: variables,
      ids: versionID ? [versionID] : id,
      totalIds: versionID ? [versionID] : id,
      filterSchemaClause,
      module: params.module_name,
      sub_module: params.submodule_name,
      audit_sub_module_identifier: firstElem,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp(),
      version: version ? version : '1',
      legacyMRN: recordId,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      application_name: applicationName,
      procedure_type,
      specimen_number_id,
      accession_number
    };

    data.sql_data = auditObj;
    try {
      if (source.current) {
        source.current.cancel();
      }
      source.current = axios.CancelToken.source();

      if (!version) {
        await getVersionsData(data);
      } else {
        const versionIdsEncryptedData = EncryptUtils.encryptedData(data);
        const versionIds = await getFileVersionData({
          tk: versionIdsEncryptedData
        });
        data.ids = versionIds?.versions_data;
        data.totalIds = versionIds?.versions_data;
      }
      if (data.ids?.length > 1 && versionID === undefined) {
        data.ids = paginateArray(
          data.totalIds,
          environment.FILE_CABINET_INIT_FETCH || documentpageNumber,
          0
        );
        setMultipleDocPresent(true);
      }
      const encryptedData = EncryptUtils.encryptedData(data);
      const response = await getFileData({tk: encryptedData}, source.current);
      const {buffer_data: responseBufferData, file_extensions} = response;
      if (file_extensions && responseBufferData) {
        setFileExtensions(file_extensions);
        setBufferData(responseBufferData);
        const value = getBase64Data(
          responseBufferData,
          file_extensions,
          data?.ids ?? []
        );

        if (!value.length) {
          setDocs([]);
          setDocDownloadData([]);
          setSelectedDocVersionIds(data?.totalIds ?? []);
          enqueueSnackbar(
            'No file content is available for the selected item.',
            SnackbarConfig.getWarning()
          );
          setIsLoading(false);
          return;
        }

        setDocs(value);
        setDocDownloadData(value);
        setSelectedDocVersionIds(data?.totalIds ?? []);
      } else {
        setDocs([]);
        setDocDownloadData([]);
        enqueueSnackbar(
          'No file content is available for the selected item.',
          SnackbarConfig.getWarning()
        );
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log('error in data fetch', err);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        console.log('error message', errMessage);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };
  const getDocumentsNextData = async (
    alreadyhasData?: boolean,
    pageSize?: number,
    pageCount?: number,
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    // setIsLoading(true);
    // if (!alreadyhasData) {
    //   setBufferData([]);
    //   setActiveDocument({uri: ''});
    //   setDocDownloadData([]);
    //   setFileExtensions([]);
    // }
    setFetchingData(true);
    const [firstElem]: any = id;

    const permissions = AuthUtils.getPermissions();

    const documentSecurityGroupId =
      updatedDocData?.DocumentSecurityGroupID ??
      docData?.DocumentSecurityGroupID;

    const documentSecurityIdExisted = Array.isArray(permissions)
      ? permissions.find((ele: any) => ele.id === documentSecurityGroupId)
      : undefined;

    const auditObj = {
      is_audit: true,
      audit_event_description:
        FormConstants.COMMON.VIEWED_DOCUMENT +
        (documentSecurityIdExisted
          ? `; User has Permission : ${documentSecurityIdExisted.function_label}`
          : '')
    };

    const data: any = {
      PatientID,
      SourceDocumentID: updatedDocData
        ? updatedDocData?.SourceDocumentID
        : (docData?.SourceDocumentID ?? ''),
      versions: documentVersionSQL,
      versionData: documentVersionDataSQL,
      sql: documentDataSql,
      variables: variables,
      ids: versionID ? [versionID] : id,
      totalIds: versionID ? [versionID] : id,
      filterSchemaClause,
      module: params.module_name,
      sub_module: params.submodule_name,
      audit_sub_module_identifier: firstElem,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp(),
      version: version ? version : '1',
      legacyMRN: recordId,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      application_name: applicationName,
      procedure_type,
      accession_number,
      specimen_number_id
    };

    data.sql_data = auditObj;
    try {
      if (source.current) {
        source.current.cancel();
      }
      source.current = axios.CancelToken.source();

      if (!version) {
        await getVersionsData(data);
      } else {
        const versionIdsEncryptedData = EncryptUtils.encryptedData(data);
        const versionIds = await getFileVersionData({
          tk: versionIdsEncryptedData
        });
        data.ids = versionIds?.versions_data;
        data.totalIds = versionIds?.versions_data;
      }
      if (alreadyhasData || data.ids?.length > 0) {
        data.ids = paginateArray(
          data.totalIds,
          pageSize || documentpageNumber,
          pageCount || docDataStateCount
        );
      }
      const encryptedData = EncryptUtils.encryptedData(data);
      const response = await getFileData({tk: encryptedData}, source.current);
      const {buffer_data: responseBufferData, file_extensions} = response;
      if (file_extensions && responseBufferData) {
        if (alreadyhasData) {
          setFileExtensions([...fileExtensions, ...file_extensions]);
          setBufferData([...bufferData, ...responseBufferData]);
          const value = getBase64NextData(
            responseBufferData,
            file_extensions,
            true,
            data?.ids ?? []
          );

          if (!value.length) {
            setFetchingData(false);
            enqueueSnackbar(
              'No additional file content is available for the selected item.',
              SnackbarConfig.getWarning()
            );
            return;
          }

          setDocs((prevDocs: any) => {
            // 1. Merge the existing docs with the new values
            const combined = [...prevDocs, ...value];

            // 2. Remove duplicates (in case the same page was fetched twice)
            const uniqueDocs = Array.from(
              new Map(combined.map(item => [item.index, item])).values()
            );

            // 3. Sort by the order in totalIds
            return uniqueDocs.sort((a, b) => {
              return (
                data.totalIds.indexOf(a.index) - data.totalIds.indexOf(b.index)
              );
            });
          });
          // setDocs([...docs, ...value]);
          setDocDownloadData((prevDocs: any) => {
            const combined = [...prevDocs, ...value];

            // 1. Remove duplicates based on index
            const uniqueDocs = Array.from(
              new Map(combined.map(item => [item.index, item])).values()
            );

            // 2. Create position map for O(1) performance
            const idPositionMap = new Map<string, number>();
            data.totalIds.forEach((id: string, idx: number) =>
              idPositionMap.set(id, idx)
            );

            // 3. Sort
            const sorted = uniqueDocs.sort((a, b) => {
              const posA = idPositionMap.get(a.index) ?? 999;
              const posB = idPositionMap.get(b.index) ?? 999;
              return posA - posB;
            });

            return sorted; // TypeScript now sees an array of objects
          });
          // setDocDownloadData([...bufferData, ...value]);
        } else {
          setFileExtensions(file_extensions);
          setBufferData(responseBufferData);
          const value = getBase64Data(
            responseBufferData,
            file_extensions,
            data?.ids ?? []
          );

          if (!value.length) {
            setDocs([]);
            setDocDownloadData([]);
            setFetchingData(false);
            enqueueSnackbar(
              'No file content is available for the selected item.',
              SnackbarConfig.getWarning()
            );
            return;
          }

          setDocs(value);
          setDocDownloadData(value);
        }
        setSelectedDocVersionIds(data?.totalIds ?? []);
      }
      setFetchingData(false);
      // setIsLoading(false);
    } catch (err) {
      console.log('error in next data fetch', err);
      // setIsLoading(false);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        console.log('error message', errMessage);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };
  const getDocumentsDownloadData = async (
    alreadyhasData?: boolean,
    pageSize?: number,
    pageCount?: number,
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    // setIsLoading(true);
    const [firstElem]: any = id;

    const permissions = AuthUtils.getPermissions();

    const documentSecurityGroupId =
      updatedDocData?.DocumentSecurityGroupID ??
      docData?.DocumentSecurityGroupID;

    const documentSecurityIdExisted = Array.isArray(permissions)
      ? permissions.find((ele: any) => ele.id === documentSecurityGroupId)
      : undefined;

    const auditObj = {
      is_audit: true,
      audit_event_description:
        FormConstants.COMMON.VIEWED_DOCUMENT +
        (documentSecurityIdExisted
          ? `; User has Permission : ${documentSecurityIdExisted.function_label}`
          : '')
    };

    const data: any = {
      PatientID,
      SourceDocumentID: updatedDocData
        ? updatedDocData?.SourceDocumentID
        : (docData?.SourceDocumentID ?? ''),
      versions: documentVersionSQL,
      versionData: documentVersionDataSQL,
      sql: documentDataSql,
      fileName: docData?.Name,
      variables: variables,
      ids: versionID ? [versionID] : id,
      totalIds: versionID ? [versionID] : id,
      filterSchemaClause,
      module: params.module_name,
      sub_module: params.submodule_name,
      audit_sub_module_identifier: firstElem,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp(),
      version: version ? version : '1',
      legacyMRN: recordId,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      application_name: applicationName,
      procedure_type,
      accession_number,
      specimen_number_id
    };

    data.sql_data = auditObj;
    try {
      if (source.current) {
        source.current.cancel();
      }
      source.current = axios.CancelToken.source();

      if (!version) {
        await getVersionsData(data);
      } else {
        const versionIdsEncryptedData = EncryptUtils.encryptedData(data);
        const versionIds = await getFileVersionData({
          tk: versionIdsEncryptedData
        });
        data.ids = versionIds?.versions_data;
        data.totalIds = versionIds?.versions_data;
      }

      const encryptedData = EncryptUtils.encryptedData(data);
      const response = await getFileZIPData({tk: encryptedData});

      const blob = new Blob([response], {type: 'application/zip'});

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docData?.Name}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      logDocumentDownload({downloadType: 'Downloaded Original'});
    } catch (err) {
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };

  useEffect(() => {
    AuthUtils.setSelectedDocID(id?.toString());

    const awaitsync = async () => {
      if ((Array.isArray(id) || typeof id === 'string') && id.length > 1) {
        await getDocumentsData();
        loadNextPage();
      } else {
        await getDocumentsData();
      }
    };
    awaitsync();
    // getDocumentsData(false);
    // loadNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setTextSelectMode(false);
  }, [id]);

  const isMultiPageTIFF = (base64Data: string): boolean => {
    // Convert base64 to ArrayBuffer
    const byteString = atob(base64Data.split(',')[1]);
    const buffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(buffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    // Parse the TIFF file
    const ifds = UTIF.decode(buffer);
    return ifds.length > 1; // Multi-page if more than one IFD (Image File Directory)
  };

  useEffect(() => {
    setIsMultiPage(false);
    if (['tif', 'tiff'].includes(fileExtensions?.[0]?.toLowerCase())) {
      const base64Data = docDownloadData[0]?.uri;
      if (base64Data) {
        const multiPage = isMultiPageTIFF(base64Data);
        setIsMultiPage(multiPage);
      }
    }
  }, [docDownloadData, fileExtensions]);

  const downloadFile = async (
    downloadData: any[],
    fileExtensions: string[],
    fileName: string
  ) => {
    setIsDownloading(true);
    logDocumentDownload({downloadType: 'Downloaded Original'});

    if (downloadData.length > 0) {
      if (downloadData?.length === 1) {
        if (
          ['rtf', 'tiff', 'tif'].includes(fileExtensions?.[0]?.toLowerCase())
        ) {
          downloadRTF(
            downloadData?.[0]?.uri,
            `${fileName}.${fileExtensions?.[0]?.toLowerCase()}`
          );
        } else {
          const a = document.createElement('a');
          a.setAttribute('href', `${downloadData?.[0]?.uri}`);
          a.setAttribute(
            'download',
            `${fileName}.${fileExtensions[0]?.toLowerCase()}`
          );
          a.click();
        }
      } else {
        const zip = new JSZip();
        downloadData.map((item: any, index: any) => {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const [fileFlag, fileData] = item?.uri?.split(',');
          const fileExtension = fileFlag
            ?.split('/')?.[1]
            ?.split(';')?.[0]
            ?.toLowerCase();

          const invalidCharacters = /[\\/:*?"<>|]/g;
          const sanitizedFileName = fileName.replace(invalidCharacters, '_');
          zip.file(`${sanitizedFileName}_${index}.${fileExtension}`, fileData, {
            base64: true
          });
        });
        zip.generateAsync({type: 'blob'}).then(function (content) {
          saveAs(content, `${fileName}.zip`);
        });
      }
    }
    setIsDownloading(false);
  };

  const downloadAllPages = async (
    isMultiPage: boolean,
    downloadData: any[],
    fileExtensions: string[],
    fileName: string
  ) => {
    if (!isMultiPage) {
      // If it's not multi-page, fallback to the standard download
      downloadFile(downloadData, fileExtensions, fileName);
      return;
    }
    setIsDownloading(true);
    // const fetched = await getDocumentsDownloadData(false);
    const base64Data = docDownloadData[0]?.uri;

    if (base64Data) {
      const zip = new JSZip();

      // Convert base64 to ArrayBuffer
      const byteString = atob(base64Data.split(',')[1]);
      const buffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(buffer);
      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }

      // Decode this file and process each page
      const ifds = UTIF.decode(buffer);

      for (let i = 0; i < ifds.length; i++) {
        UTIF.decodeImage(buffer, ifds[i]);
        const rgba = UTIF.toRGBA8(ifds[i]);
        const width = ifds[i].width;
        const height = ifds[i].height;

        // Create canvas to render file
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // Draw this page as ImageData
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to Original Blob and add to ZIP file
        const blob = await new Promise<Blob | null>(resolve =>
          canvas.toBlob(resolve, `image/${fileExtensions}`)
        );
        const invalidCharacters = /[\\/:*?"<>|]/g;
        const sanitizedFileName = fileName.replace(invalidCharacters, '_');
        if (blob) {
          zip.file(`${sanitizedFileName}_${i + 1}.${fileExtensions}`, blob);
        }
      }

      // Generate ZIP file and trigger download
      zip.generateAsync({type: 'blob'}).then(function (content) {
        saveAs(content, `${fileName}.zip`);
      });
    }
    setIsDownloading(false);
  };

  // Sends Document Details, instead of data stream
  const downloadFileAsPDF = async (
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    setIsDownloading(true);

    if (
      fileExtensions?.length === 1 &&
      fileExtensions[0]?.toLowerCase() === 'pdf' &&
      docDownloadData?.length === 1
    ) {
      try {
        logDocumentDownload({downloadType: 'Download as PDF'});
        const link = document.createElement('a');
        link.href = docDownloadData[0]?.uri;
        link.setAttribute(
          'download',
          `${cardValues?.[0]?.name || 'download'}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    const tempObj: any = docVersionsData?.filter(
      (item: any) => item?.Version == version
    )[0];

    versionID = selectedDocVersionIds?.length
      ? selectedDocVersionIds
      : tempObj?.VersionID ?? id;
    updatedDocData = tempObj ? {...docData, ...tempObj} : docData;

    const auditObj = {
      is_audit: true,
      audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT
    };

    const [firstElem]: any = id;

    const data: any = {
      PatientID,
      SourceDocumentID: updatedDocData
        ? updatedDocData?.SourceDocumentID
        : (docData?.SourceDocumentID ?? ''),
      versions: documentVersionSQL,
      versionData: documentVersionDataSQL,
      sql: documentDataSql,
      variables: variables,
      ids: versionID,
      filterSchemaClause,
      module: params.module_name,
      sub_module: params.submodule_name,
      audit_sub_module_identifier: firstElem,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp(),
      version: version ? version : '1',
      legacyMRN: recordId,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      application_name: applicationName,
      procedure_type,
      accession_number,
      specimen_number_id
    };

    data.sql_data = auditObj;

    try {
      const encryptedData = EncryptUtils.encryptedData(data);
      const response = await downloadAsPdf({tk: encryptedData});
      logDocumentDownload({downloadType: 'Download as PDF', ...data});
      const url = window.URL.createObjectURL(
        new Blob([response], {type: 'application/pdf'})
      );

      // Create a temporary anchor element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cardValues?.[0]?.name}.pdf`); // The file name
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setIsLoading(false);
      if (err) {
        const errMessage = getErrorMessage(err);
        console.error('Error during file download:', errMessage);
        enqueueSnackbar(
          errMessage?.length ? errMessage : 'Error during file download',
          SnackbarConfig.getError()
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDocumentChange = (document: any) => {
    setActiveIndex(document.index);
    setActiveDocument(document);
    syncActiveDocumentType(document);
    const currentFileNo = docs.findIndex((d: any) => d.uri === document.uri);
    setDocViewerState(prev => ({
      ...prev,
      currentFileNo: currentFileNo === -1 ? 0 : currentFileNo,
      currentDocument: document,
      documents: docs
    }));
  };

  const renderPreviewer = (): any => {
    let initialPreviewer: any;

    // fileExtensions.forEach(eachExtension => {
    //   if (!SupportedFormats.includes(eachExtension.toUpperCase())) {
    //     initialPreviewer = (
    //       <TextContainer
    //         height={height}
    //         width={'min-content'}
    //         maxWidth={'50vw'}>
    //         <Typography variant="h6">
    //           {t('PRIVATE.DOCUMENT.NO_PREVIEW_AVAILABLE', {
    //             ns: 'private'
    //           })}
    //         </Typography>
    //       </TextContainer>
    //     );
    //   }
    // });
const currentFileNo = docs.findIndex(
      (eachDoc: any) => eachDoc?.uri === activeDocument?.uri
    );
    const activeExtension =
      fileExtensions[currentFileNo === -1 ? 0 : currentFileNo] ?? '';
      // console.log('activeExtension', activeExtension);
    const isActivePageUnsupported =
      !!activeExtension &&
      !SupportedFormats.includes(activeExtension.toUpperCase());

    if (isActivePageUnsupported) {
      initialPreviewer = (
        <TextContainer height={height} width={'min-content'} maxWidth={'50vw'}>
          <Typography variant="h6">
            {t('PRIVATE.DOCUMENT.NO_PREVIEW_AVAILABLE', {
              ns: 'private'
            })}
          </Typography>
        </TextContainer>
      );
    }
    if (!initialPreviewer) {
      if (
        !iframeSupportedFormats.includes(fileExtensions[0]?.toLowerCase()) &&
        typeof activeDocument?.uri === 'string' &&
        docs?.length
      ) {
        initialPreviewer = (
          <PreviewerContainer
            style={
              isHtmlFile
                ? {
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    overflow: 'auto'
                  }
                : undefined
            }>
            <DocViewer
              documents={docs}
              pluginRenderers={[
                ...DocViewerRenderers,
                HTMLRenderer,
                TiffRenderer,
                RTFRenderer,
                TxtRenderer,
                ReactPDFRenderer
              ]}
              initialActiveDocument={activeDocument}
              activeDocument={activeDocument}
              onDocumentChange={handleDocumentChange}
              config={{
                header: {
                  disableHeader: true // ← header is now rendered outside
                },
                pdfVerticalScrollByDefault: true
              }}
              style={{
                paddingBottom: '3rem',
                width: '100%',
                height: '100%',
                minWidth: '45vw',
                minHeight: '90vh',
                backgroundColor: '#F4F6F8'
              }}
            />
          </PreviewerContainer>
        );
      }
    }
    return initialPreviewer;
  };

  const getPreviewer = React.useMemo(() => {
    if (fileExtensions.length > 1) {
      return (
        <TransformWrapper
          ref={transformComponentRef}
          initialScale={1}
          initialPositionX={0}
          initialPositionY={0}
          minScale={0.5}
          maxScale={10}
          limitToBounds={true}
          doubleClick={{disabled: true}}
          wheel={{
            disabled: true,
            wheelDisabled: true,
            touchPadDisabled: true
          }}>
          {({...rest}) => (
            <MainContainerDiv>
              <TransformComponent
                contentStyle={{
                  display: 'flex',
                  position: 'relative',
                  alignItems: 'center',
                  justifyContent: 'start',
                  marginLeft: '10px',
                  overflow: 'hidden',
                  minWidth: '53vw',
                  minHeight: '60vh'
                  // marginTop: '150px',
                }}>
                {renderPreviewer()}
              </TransformComponent>
            </MainContainerDiv>
          )}
        </TransformWrapper>
      );
    }
    return (
      <Box>
        {textSelectMode ? (
          <MainContainerDiv
            style={{
              display: 'flex',
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'start',
              marginLeft: '10px',
              overflow: 'hidden',
              minWidth: '53vw',
              minHeight: '60vh'
            }}>
            {renderPreviewer()}
          </MainContainerDiv>
        ) : (
          activeDocument?.uri && (
            <TransformWrapper
              ref={transformComponentRef}
              initialScale={1}
              initialPositionX={0}
              initialPositionY={0}
              minScale={0.5}
              maxScale={10}
              limitToBounds={true}
              doubleClick={{disabled: true}}
              wheel={{
                disabled: true,
                wheelDisabled: true,
                touchPadDisabled: true
              }}>
              {({...rest}) => (
                <MainContainerDiv>
                  <TransformComponent
                    contentStyle={{
                      display: 'flex',
                      position: 'relative',
                      alignItems: 'center',
                      justifyContent: 'start',
                      marginLeft: '10px',
                      overflow: 'hidden',
                      minWidth: '53vw',
                      minHeight: '60vh'
                      // marginTop: '150px',
                    }}>
                    {renderPreviewer()}
                  </TransformComponent>
                </MainContainerDiv>
              )}
            </TransformWrapper>
          )
        )}
      </Box>
    );
  }, [textSelectMode, activeDocument?.uri, docs, fetchingData]);

  const viewDocumentAction = React.useCallback((): ReactElement => {
    return (
      <Stack
        sx={{
          px: 2
        }}>
        <LoadingButton
          type="submit"
          variant={shoulddisplaymetainfo ? 'outlined' : 'contained'}
          onClick={() => setShoulddisplaymetainfo(!shoulddisplaymetainfo)}
          sx={{
            bgcolor: theme.palette.primary.main
          }}>
          {shoulddisplaymetainfo
            ? t('PRIVATE.DOCUMENT.HIDE_DETAILS', {
                ns: 'private'
              })
            : t('PRIVATE.DOCUMENT.VIEW_DETAILS', {
                ns: 'private'
              })}
        </LoadingButton>
      </Stack>
    );
  }, [shoulddisplaymetainfo, theme]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const downloadDocumentAction = React.useCallback((): ReactElement => {
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (option: string) => {
      if (option === 'Download As PDF') {
        downloadFileAsPDF(selectedDocVersion);
      } else {
        multipleDocPresent
          ? getDocumentsDownloadData()
          : isMultiPage
            ? downloadAllPages(
                isMultiPage,
                docDownloadData,
                fileExtensions,
                cardValues?.[0]?.name || 'download'
              )
            : downloadFile(
                docDownloadData,
                fileExtensions,
                cardValues?.[0]?.name || 'download'
              );
      }
      handleClose();
    };

    const options = ['Download Original', 'Download As PDF'];

    return (
      <Stack direction="row" spacing={2} sx={{pr: 2}}>
        <ButtonGroup variant="contained">
          <LoadingButton
            loading={isDownloading}
            sx={{
              bgcolor: theme.palette.primary.dark,
              color: 'white',
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
              '&:hover': {bgcolor: theme.palette.primary.main},
              paddingLeft: '16px'
            }}
            onClick={() =>
              multipleDocPresent
                ? getDocumentsDownloadData()
                : isMultiPage
                  ? downloadAllPages(
                      isMultiPage,
                      docDownloadData,
                      fileExtensions,
                      cardValues?.[0]?.name || 'download'
                    )
                  : downloadFile(
                      docDownloadData,
                      fileExtensions,
                      cardValues?.[0]?.name || 'download'
                    )
            }>
            Download
          </LoadingButton>
          <Button
            disabled={isDownloading}
            size="small"
            sx={{
              bgcolor: theme.palette.primary.dark,
              color: 'white',
              borderTopRightRadius: 'inherit !important',
              borderBottomRightRadius: 'inherit !important',
              height: 'auto !important',
              minWidth: 'auto',
              '&:hover': {bgcolor: theme.palette.primary.main}
            }}
            aria-controls={anchorEl ? 'split-button-menu' : undefined}
            // eslint-disable-next-line no-extra-boolean-cast
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={handleClick}>
            <ArrowDropDownIcon />
          </Button>
          <Menu
            id="split-button-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}>
            {options.map(option => (
              <MenuItem
                key={option}
                onClick={() => handleMenuItemClick(option)}>
                {option}
              </MenuItem>
            ))}
          </Menu>
        </ButtonGroup>
      </Stack>
    );
  }, [
    isDownloading,
    selectedDocVersion,
    anchorEl,
    docDownloadData,
    fileExtensions,
    cardValues,
    isMultiPage,
    isHtmlFile
  ]);
  // console.log('fileExtensions-->',fileExtensions);
  // useEffect(() => {
  //   if (bufferData.length && fileExtensions.length) {
  //     const fileExtension = fileExtensions[0];

  //     // Ensure the document is in a supported format
  //     if (!unSupportedFormats.includes(fileExtension)) {
  //       const firstDocument = {
  //         uri: `${getBase64Flag(fileExtension)}${Buffer.from(
  //           bufferData[0].data
  //         )?.toString('base64')}`,
  //         index: 0
  //       };

  //       // Prevent unnecessary updates
  //       if (!activeDocument || activeDocument.uri !== firstDocument.uri) {
  //         setActiveDocument(firstDocument);
  //       }
  //     }
  //   }
  // }, [bufferData, fileExtensions]);

  useEffect(() => {
    getPreviewer;
  }, [docs, activeDocument]);

  const handleChangeDocVersion = (version: string) => {
    setSelectedDocVersion(version);
    const tempObj: any = docVersionsData?.filter(
      (item: any) => item?.Version == version
    )[0];
    setDocData({...docData, ...tempObj});
    getDocumentsData(version, tempObj?.VersionID, {
      ...docData,
      ...tempObj
    });
  };

  const renderSelectFields = (): ReactElement => {
    return (
      <SelectInput
        enableDefaultOption
        options={docVersionsData || []}
        placeholder={'Select Version'}
        name={'doc_versions_dropdown'}
        value={selectedDocVersion}
        onChange={(e: any) => handleChangeDocVersion(e.target.value)}
        optionValueKey={'Version'}
        optionLabelKey={'VersionLabel'}
        selectProps={{
          size: 'small'
        }}
      />
    );
  };
  const Controls: React.FC<ControlsProps> = ({
    zoomIn,
    zoomOut,
    resetTransform
  }) => {
    return (
      <ZoomButtonsDiv className="tools">
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_IN', {
              ns: 'private'
            })}
            onClick={zoomIn}>
            <ZoomIn
              sx={{
                color: theme.palette.common.white,
                fontSize: {xs: 24, sm: 22, md: 20, lg: 18, xl: 16}
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_OUT', {
              ns: 'private'
            })}
            onClick={zoomOut}>
            <ZoomOut
              sx={{
                color: theme.palette.common.white,
                fontSize: {xs: 24, sm: 22, md: 20, lg: 18, xl: 16}
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={resetTransform}>
            <Refresh
              sx={{
                color: theme.palette.common.white,
                fontSize: {xs: 24, sm: 22, md: 20, lg: 18, xl: 16}
              }}
            />
          </ZoomButton>
        )}
        {(!fileExtensions[0]?.toLowerCase().includes('txt') &&
          !textSelectMode &&
          ZoomInShowTextRef.current > 0) ||
        ZoomInShowTextRef.current < 0 ? (
          <PanToMoveDiv>
            {t('PRIVATE.DOCUMENT.MOVE_DOCUMENT', {
              ns: 'private'
            })}
          </PanToMoveDiv>
        ) : null}
        {['pdf', 'rtf'].includes(fileExtensions[0]?.toLowerCase()) && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={() => {
              setTextSelectMode(prev => !prev);
            }}>
            {textSelectMode ? (
              <DocumentScanner
                sx={{
                  color: theme.palette.common.white,
                  fontSize: {xs: 24, sm: 22, md: 20, lg: 18, xl: 16}
                }}
              />
            ) : (
              <Title
                sx={{
                  color: theme.palette.common.white,
                  fontSize: {xs: 24, sm: 22, md: 20, lg: 18, xl: 16}
                }}
              />
            )}
          </ZoomButton>
        )}
      </ZoomButtonsDiv>
    );
  };
  const renderButtons = React.useCallback((): ReactElement => {
    return (
      <AdditionalFeaturesButtonDiv>
        {customDocViewerActions.map((item: any) => {
          if (item.action_name === FormConstants.DOCUMENTS_ACTION_TYPES.VIEW) {
            return viewDocumentAction();
          } else if (
            item.action_name ===
              FormConstants.DOCUMENTS_ACTION_TYPES.DOWNLOAD &&
            docDownloadData?.length > 0
          ) {
            return downloadDocumentAction();
          } else {
            return null;
          }
        })}
        <Controls
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetTransform={resetTransform}
        />
        {isHtmlFile && (
          <>
            <ZoomButton
              style={{marginTop: '3px'}}
              title={htmlTextSelect ? 'Pan mode' : 'Text select mode'}
              onClick={() => {
                // htmlViewerRef.current?.toggleTextSelect();
                setHtmlTextSelect(prev => !prev);
              }}>
              {htmlTextSelect ? (
                <DocumentScanner sx={{color: theme.palette.common.white}} />
              ) : (
                <Title sx={{color: theme.palette.common.white}} />
              )}
            </ZoomButton>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 1,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'text.secondary',
                userSelect: 'none'
              }}>
              {htmlZoom}%
            </Box>
          </>
        )}
      </AdditionalFeaturesButtonDiv>
    );
  }, [
    customDocViewerActions,
    docDownloadData,
    zoomIn,
    zoomOut,
    resetTransform,
    isHtmlFile,
    htmlZoom,
    htmlTextSelect
  ]);

  return isLoading ? (
    <DocViewContainer isMultiPage={fileExtensions?.length > 1} height={height}>
      <ComponentLoader loaderSize={10} />
    </DocViewContainer>
  ) : (
    <Stack
      direction={'column'}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        py: 2
      }}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingBottom: 1,
            borderBottom: '1px dotted',
            borderColor: 'gray',
            gap: 1
          }}>
          <Box sx={{flexShrink: 0}}>{renderButtons()}</Box>
          {docs.length > 1 && (
            <Box sx={{ml: {xs: 1, sm: 1}, flexShrink: 1, minWidth: 0}}>
              <DocHeader
                id={id}
                docs={docs}
                fetchingData={fetchingData}
                documentpageNumber={documentpageNumber}
                pendingPageJumpRef={pendingPageJumpRef}
                enqueueSnackbar={enqueueSnackbar}
                getDocumentsNextData={getDocumentsNextData}
                loadNextPage={loadNextPage}
                onGoTo={handleGoTo}
                docViewerState={docViewerState}
              />
            </Box>
          )}
          <Box sx={{px: {xs: 1, sm: 1}, flexShrink: 0}}>
            {docVersionsData?.length ? renderSelectFields() : null}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          top: '0',
          width: '100%',
          // overflowY: 'scroll'
          overflowY:
            fileExtensions?.includes('PDF') ||
            fileExtensions?.includes('pdf') ||
            fileExtensions?.includes('HTML') ||
            fileExtensions?.includes('html')
              ? 'hidden'
              : 'scroll'
        }}>
        <DocViewContainer
          isMultiPage={fileExtensions?.length > 1}
          height={height}>
          {docs.length === 0 ? (
            <TextContainer height={height} width={'100%'} maxWidth={'50vw'}>
              <Typography variant="h6">No file or folder found.</Typography>
              <Typography variant="body2" color="text.secondary">
                The selected item does not have readable file content.
              </Typography>
            </TextContainer>
          ) : (
            getPreviewer
          )}
        </DocViewContainer>
      </Box>
    </Stack>
  );
};

export default CustomDocViewer;

